// "What Is Your Body Asking For?" — A Wellness Pattern Assessment
// 6 systems x 5 statements. Each statement scored 0-4 (Never -> Almost Always).
// Section score = sum of its 5 answers (max 20).

export const scaleOptions = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Rarely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Almost Always' },
];

export const sections = [
  {
    key: 'nervous-system',
    name: 'Nervous System Overload',
    icon: 'Brain',
    accent: 'violet',
    questions: [
      'I feel tired but have difficulty relaxing.',
      'I often feel overwhelmed by noise, crowds, demands, or stimulation.',
      'I find myself constantly "doing" and struggle to slow down.',
      'I wake during the night or have difficulty getting restful sleep.',
      'I feel disconnected from my body or unsure what it needs.',
    ],
    interpretation: 'Your body may be spending more time in protection than repair.',
    support: [
      'Fascial Maneuvers',
      'Breathwork',
      'Meditation',
      'Sleep restoration',
      'Nervous system regulation practices',
      'Slow Wellness',
    ],
  },
  {
    key: 'inflammation',
    name: 'Inflammation Load',
    icon: 'Flame',
    accent: 'rose',
    questions: [
      'I wake feeling stiff, achy, or swollen.',
      'My energy tends to crash throughout the day.',
      'My body feels puffy, inflamed, or heavy.',
      'I experience recurring headaches, joint discomfort, or body aches.',
      'My symptoms worsen after sugar, alcohol, processed foods, or poor sleep.',
    ],
    interpretation: 'Your body may be responding to ongoing stressors that are creating excess wear and tear.',
    support: [
      'Anti-inflammatory nutrition',
      'Improved sleep',
      'Stress reduction',
      'Movement',
      'Hydration',
      'Terrain support',
    ],
  },
  {
    key: 'gut-barrier',
    name: 'Gut & Barrier Stress',
    icon: 'Sprout',
    accent: 'amber',
    questions: [
      'I experience bloating, gas, reflux, or digestive discomfort.',
      'Certain foods seem to bother me more than they used to.',
      'I feel sluggish, sleepy, or foggy after eating.',
      'My digestion feels inconsistent or unpredictable.',
      'I experience skin issues, mood changes, or fatigue that seem connected to digestion.',
    ],
    interpretation: 'Your digestive system may be asking for additional support.',
    support: [
      'Food quality improvements',
      'Digestive support',
      'Elimination protocols',
      'Feel Good Detox',
      'C.R.A.P. Cleanse',
    ],
  },
  {
    key: 'detox-drainage',
    name: 'Detox & Drainage Support',
    icon: 'Droplets',
    accent: 'teal',
    questions: [
      'I often feel "stuck" physically, mentally, or emotionally.',
      'I sweat very little, even during activity.',
      'I tend to retain fluid or feel congested and puffy.',
      'My bowel movements are inconsistent or less than ideal.',
      'I feel better after movement, stretching, massage, or bodywork.',
    ],
    interpretation: "Your body's elimination pathways may benefit from extra attention.",
    support: [
      'Hydration',
      'Lymphatic support',
      'Fascial work',
      'Sweating',
      'Movement',
      'Minerals',
    ],
  },
  {
    key: 'environmental',
    name: 'Environmental Burden',
    icon: 'Wind',
    accent: 'sky',
    questions: [
      'I am sensitive to fragrances, chemicals, cleaning products, or strong smells.',
      'I experience brain fog, fatigue, or headaches without a clear explanation.',
      'I feel noticeably better outdoors or while traveling.',
      'I have lived or worked in a building with water damage, dampness, or visible mold.',
      'My symptoms seem difficult to connect to any one cause.',
    ],
    interpretation: 'Your body may be responding to factors in your environment that deserve further exploration.',
    support: [
      'Air quality awareness',
      'Reducing chemical exposure',
      'Home and workplace evaluation',
      'Nervous system regulation',
      'Terrain support',
    ],
  },
  {
    key: 'microbial',
    name: 'Microbial Imbalance Patterns',
    icon: 'Bug',
    accent: 'emerald',
    questions: [
      'I crave sugar, refined carbohydrates, or sweets regularly.',
      'My symptoms seem to come and go in cycles.',
      'I frequently wake between 1:00am and 4:00am.',
      'I experience unexplained skin irritation, itching, or recurring digestive symptoms.',
      'Despite trying many wellness approaches, I feel like something is still "off."',
    ],
    interpretation: 'Your responses suggest there may be value in exploring gut ecology and overall terrain balance.',
    support: [
      'Digestive restoration',
      'Nutritional support',
      'Professional assessment',
      'Detoxification support',
      'Terrain-based approaches',
    ],
  },
  {
    key: 'post-viral',
    name: 'Post-Viral Recovery & Resilience',
    icon: 'HeartPulse',
    accent: 'indigo',
    questions: [
      'I feel like my energy has never fully returned after an illness.',
      'Physical activity sometimes leaves me unusually exhausted or needing extended recovery.',
      'I experience brain fog, difficulty concentrating, or memory lapses more than I used to.',
      'My stamina, resilience, or ability to handle stress feels lower than it once was.',
      'I often feel like I am functioning below my previous baseline.',
    ],
    interpretation: 'Your responses suggest your body may still be rebuilding energy reserves and resilience following illness, stress, or prolonged life demands.',
    support: [
      'Nervous system regulation',
      'Sleep restoration',
      'Gradual movement progression',
      'Mitochondrial support',
      'Breathwork',
      'Recovery-focused wellness practices',
      'Pacing and energy management',
    ],
    closing: 'Your body may not need more effort. It may need better recovery.',
  },
];

// Optional deeper-dive questions — captured and saved, but kept separate from the 0-20 banding.
export const deeperDive = {
  key: 'deeper-dive',
  name: 'Optional — Deeper Dive',
  icon: 'Activity',
  accent: 'slate',
  intro: 'These five are optional. They help paint a fuller picture of how your body responds day to day. Answer the ones that feel relevant, or skip straight to your results.',
  questions: [
    'I experience dizziness, lightheadedness, or feeling "off" when standing.',
    'My heart rate feels unusually reactive to activity, stress, or exertion.',
    'Heat, humidity, or overexertion affects me more than it used to.',
    'I seem to have developed new sensitivities since a significant illness.',
    'Some days I feel significantly better or worse without obvious explanation.',
  ],
  note: "These responses are saved with your assessment to help paint a fuller picture over time. They aren't scored — they simply add context.",
};

// Load bands by section score (0-20)
export const bands = [
  {
    key: 'low',
    label: 'Low Load',
    min: 0,
    max: 5,
    color: 'emerald',
    copy: 'This area appears relatively balanced at the moment. Continue supporting the habits and routines that are helping you stay resilient.',
  },
  {
    key: 'mild',
    label: 'Mild Load',
    min: 6,
    max: 10,
    color: 'amber',
    copy: 'Your body may be beginning to compensate in this area. Small shifts often create significant improvements when addressed early.',
  },
  {
    key: 'moderate',
    label: 'Moderate Load',
    min: 11,
    max: 15,
    color: 'orange',
    copy: 'This system may be working harder than it should. Consider making this a priority area for support and restoration.',
  },
  {
    key: 'high',
    label: 'High Load',
    min: 16,
    max: 20,
    color: 'rose',
    copy: 'Your responses suggest this may be one of the primary areas asking for attention. Supporting this system may create ripple effects throughout many other aspects of your health and well-being.',
  },
];

export const getBand = (score) => bands.find((b) => score >= b.min && score <= b.max) || bands[0];

export const finalReflectionOptions = [
  'My body needs more rest.',
  'My body needs more nourishment.',
  'My body needs more movement.',
  'My body needs less stress.',
  "My body needs more support than I've been giving it.",
  "I'm ready to start listening more closely.",
];
